// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ScanLine, ClipboardList, MapPin, CalendarDays } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import MyOfficiatingSection from "@/components/staff/MyOfficiatingSection";
import { formatCalendarDate } from "@/lib/dates";

type StaffAssignment = {
  assignment_id: string;
  event: {
    id: string;
    name: string;
    slug?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    status?: string | null;
  };
  court: { id: string; name: string } | null;
  court_scoped: boolean;
  role?: string | null;
  can_check_in: boolean;
  can_score: boolean;
};

function formatDateRange(start?: string | null, end?: string | null) {
  if (!start) return null;
  const s = formatCalendarDate(start, "MMM d");
  if (!end || end === start) return s;
  return `${s} – ${formatCalendarDate(end, "MMM d")}`;
}

/** A large, one-handed tap target. */
function LaunchButton({
  icon: Icon,
  label,
  onClick,
  variant,
  dominant,
}: {
  icon: any;
  label: string;
  onClick: () => void;
  variant: "checkin" | "score";
  dominant?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-4 rounded-2xl border px-5 text-left font-semibold transition active:scale-[0.99]",
        dominant ? "py-8 text-xl" : "py-5 text-lg",
        variant === "checkin"
          ? "border-primary/20 bg-primary/10 text-primary hover:bg-primary/15"
          : "border-secondary/30 bg-secondary/10 text-secondary-foreground hover:bg-secondary/20"
      )}
    >
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-xl",
          dominant ? "h-14 w-14" : "h-11 w-11",
          variant === "checkin" ? "bg-primary/15 text-primary" : "bg-secondary/20 text-secondary-foreground"
        )}
      >
        <Icon className={dominant ? "h-7 w-7" : "h-5 w-5"} />
      </span>
      <span>{label}</span>
    </button>
  );
}

export default function StaffHome() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staffName, setStaffName] = useState<string>("");
  const [assignments, setAssignments] = useState<StaffAssignment[]>([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?redirect=/staff/home");
      return;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fnError } = await invokeEdgeFunction(
          "rle-staff-home",
          { body: {} }
        );
        if (fnError || (data && data.ok === false)) {
          setError(data?.error || fnError?.message || "Could not load your assignments");
          setLoading(false);
          return;
        }
        setStaffName(data?.staff_name ?? "");
        setAssignments(data?.assignments ?? []);
      } catch (e: any) {
        setError(e?.message || "Could not load your assignments");
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading]);

  // Group assignments by event.
  const eventGroups = useMemo(() => {
    const map = new Map<string, { event: StaffAssignment["event"]; items: StaffAssignment[] }>();
    for (const a of assignments) {
      const key = a.event.id;
      if (!map.has(key)) map.set(key, { event: a.event, items: [] });
      map.get(key)!.items.push(a);
    }
    return Array.from(map.values());
  }, [assignments]);

  const goCheckIn = (a: StaffAssignment) => navigate("/staff/check-in");
  const goScore = (a: StaffAssignment) => navigate(`/staff/events/${a.event.id}`);

  // Single assignment + single capability → one dominant button.
  const totalCapabilities = assignments.reduce(
    (n, a) => n + (a.can_check_in ? 1 : 0) + (a.can_score ? 1 : 0),
    0
  );
  const singleDominant = assignments.length === 1 && totalCapabilities === 1;

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="rounded-2xl border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
        <MyOfficiatingSection />
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <div className="space-y-8">
        <div className="flex min-h-[30vh] flex-col items-center justify-center gap-3 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            <CalendarDays className="h-8 w-8 text-muted-foreground" />
          </div>
          <h1 className="text-xl font-semibold">No assignments yet</h1>
          <p className="max-w-xs text-sm text-muted-foreground">
            Check with your event organizer.
          </p>
        </div>
        <MyOfficiatingSection />
      </div>
    );
  }

  const primaryEventName = eventGroups[0]?.event?.name;

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <p className="text-sm font-medium text-muted-foreground">
          {staffName ? `You're working, ${staffName}` : "You're working"}
        </p>
        {eventGroups.length === 1 && primaryEventName && (
          <h1 className="text-2xl font-bold leading-tight">{primaryEventName}</h1>
        )}
      </header>

      {eventGroups.map(({ event, items }) => (
        <section key={event.id} className="space-y-4">
          {eventGroups.length > 1 && (
            <div className="space-y-0.5">
              <h2 className="text-xl font-bold leading-tight">{event.name}</h2>
              {formatDateRange(event.start_date, event.end_date) && (
                <p className="text-sm text-muted-foreground">
                  {formatDateRange(event.start_date, event.end_date)}
                </p>
              )}
            </div>
          )}

          <div className="space-y-5">
            {items.map((a) => (
              <div
                key={a.assignment_id}
                className="space-y-3 rounded-2xl border bg-card p-4 sm:p-5"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span className="font-medium text-foreground">
                    {a.court_scoped && a.court ? `Court ${a.court.name}` : "All courts"}
                  </span>
                  {a.role && <span className="text-muted-foreground">· {a.role}</span>}
                </div>

                <div className="space-y-3">
                  {a.can_check_in && (
                    <LaunchButton
                      icon={ScanLine}
                      label="Check-In"
                      variant="checkin"
                      dominant={singleDominant}
                      onClick={() => goCheckIn(a)}
                    />
                  )}
                  {a.can_score && (
                    <LaunchButton
                      icon={ClipboardList}
                      label="Live Scoring"
                      variant="score"
                      dominant={singleDominant}
                      onClick={() => goScore(a)}
                    />
                  )}
                  {!a.can_check_in && !a.can_score && (
                    <p className="rounded-xl bg-muted px-4 py-3 text-sm text-muted-foreground">
                      No actions available for this assignment yet.
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      <MyOfficiatingSection />
    </div>
  );
}
